/**
 * The "How should we help?" dialog (TMMAPP-0120 items 1–4): the consent step under one title, with
 * Save (it saves exactly what Continue saved: `PATCH /me {aiEnabled}` first, then the automation
 * choices with `reviewed: true`) and Not now (closes, saving nothing; a moved switch is dropped
 * with no confirm, and only then a notice says nothing was changed). `hideFor` adds the "Don't show
 * this again" box, used only where the dialog opens on its own (My Accounts on arrival): when it is
 * ticked and the dialog closes either way, the arrival stops opening it for this person on this
 * browser. Full screen below `sm` only (G11).
 */
import { useCallback, useId, useRef, useState } from 'react';
import { Checkbox, Dialog, DialogTitle, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AutomationCatalog } from '@/api/automation';
import { useNotify } from '@/shell/NotifyProvider';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { ConsentStep } from './ConsentStep';
import { writeHowWeHelpHidden } from './howWeHelpHidden';

interface HowWeHelpDialogProps {
  catalog: AutomationCatalog;
  /** Not now, Escape: closed without saving. */
  onClose: () => void;
  /** Save succeeded (the choices are stamped reviewed); the host closes and continues. */
  onSaved: () => void;
  onCatalogChange?: ((catalog: AutomationCatalog) => void) | undefined;
  /** The person whose "Don't show this again" this dialog offers; absent, no box. */
  hideFor?: number | null | undefined;
}

export function HowWeHelpDialog({
  catalog,
  onClose,
  onSaved,
  onCatalogChange,
  hideFor,
}: HowWeHelpDialogProps) {
  const { t } = useTranslation();
  const notify = useNotify();
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const [hide, setHide] = useState(false);
  const offersHide = hideFor !== undefined;
  const status = useRef({ busy: false, changed: false });
  const onStatus = useCallback((next: { busy: boolean; changed: boolean }) => {
    status.current = next;
  }, []);

  function rememberHide() {
    if (offersHide && hide) writeHowWeHelpHidden(hideFor ?? null);
  }

  /** Not now, Escape and the backdrop: closed unsaved, saying so only if a switch moved. */
  function cancel(changed: boolean) {
    rememberHide();
    onClose();
    if (changed) notify(t('consent.nothingChanged'));
  }

  return (
    <Dialog
      open
      onClose={() => {
        // A save in flight finishes first: Escape never drops it half way.
        if (status.current.busy) return;
        cancel(status.current.changed);
      }}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId}>{t('consent.title')}</DialogTitle>
      <ConsentStep
        variant="full"
        layout="dialog"
        catalog={catalog}
        onCatalogChange={onCatalogChange}
        onCompleted={() => {
          rememberHide();
          onSaved();
        }}
        onCancel={cancel}
        onStatus={onStatus}
        footerStart={
          offersHide ? (
            <FormControlLabel
              control={<Checkbox checked={hide} onChange={(_event, next) => setHide(next)} />}
              label={t('consent.dontShowAgain')}
            />
          ) : undefined
        }
      />
    </Dialog>
  );
}
